import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { listAudit, listAuditActions, type Cursor } from '@/api/ops';
import type { AuditEntry } from '@/api/opsTypes';
import { partnerOptions } from '@/api/partners';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/ui/DataTable';
import { KeysetMore } from '@/ui/KeysetMore';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';

const ALL = 'ALL';
const ACTOR_TYPES = ['PLATFORM_ADMIN', 'PARTNER_KEY', 'ORG_KEY', 'USER', 'SYSTEM'];

const columns: ColumnDef<AuditEntry, never>[] = [
  {
    id: 'when',
    header: 'When',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
  },
  {
    id: 'actor',
    header: 'Actor',
    cell: ({ row }) => row.original.actor_email ?? row.original.actor_type,
  },
  { accessorKey: 'action', header: 'Action' },
  {
    id: 'target',
    header: 'Target',
    cell: ({ row }) =>
      row.original.target_type
        ? `${row.original.target_type} ${row.original.target_id ?? ''}`
        : '—',
  },
  {
    id: 'detail',
    header: 'Detail',
    cell: ({ row }) => (
      <dl className="flex flex-col text-xs">
        {Object.entries(row.original.detail ?? {}).map(([key, value]) => (
          <div key={key} className="flex gap-1">
            <dt className="text-muted-foreground">{key}:</dt>
            <dd className="whitespace-normal break-all">
              {typeof value === 'string' ? value : JSON.stringify(value)}
            </dd>
          </div>
        ))}
      </dl>
    ),
  },
  { id: 'ip', header: 'IP', cell: ({ row }) => row.original.ip ?? '—' },
];

export function AuditPage() {
  const [actorType, setActorType] = useState(ALL);
  const [action, setAction] = useState(ALL);
  const [partnerId, setPartnerId] = useState(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const rangeInvalid = from !== '' && to !== '' && to < from;

  const actions = useQuery({ queryKey: ['audit', 'actions'], queryFn: listAuditActions });
  const partners = useQuery({ queryKey: ['partners', 'options'], queryFn: partnerOptions });

  const filters = {
    ...(actorType !== ALL ? { actor_type: actorType } : {}),
    ...(action !== ALL ? { action } : {}),
    ...(partnerId !== ALL ? { partner_id: partnerId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const query = useInfiniteQuery({
    queryKey: ['audit', filters],
    queryFn: ({ pageParam }) => listAudit(filters, pageParam),
    initialPageParam: undefined as Cursor | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: !rangeInvalid,
  });

  const pick = (
    label: string,
    value: string,
    set: (v: string) => void,
    options: [string, string][],
  ) => (
    <Select value={value} onValueChange={set}>
      <SelectTrigger aria-label={label} className="w-52">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{`All ${label.toLowerCase()}s`}</SelectItem>
        {options.map(([id, name]) => (
          <SelectItem key={id} value={id}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <>
      <PageHeader title="Audit log" description="Newest first, 50 at a time." />
      <div className="flex flex-wrap items-center gap-2">
        {pick(
          'Actor type',
          actorType,
          setActorType,
          ACTOR_TYPES.map((t) => [t, t]),
        )}
        {pick(
          'Action',
          action,
          setAction,
          (actions.data ?? []).map((a) => [a, a]),
        )}
        {pick(
          'Partner',
          partnerId,
          setPartnerId,
          (partners.data ?? []).map((p) => [p.id, p.name]),
        )}
        <Input
          aria-label="From"
          type="date"
          className="w-40"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <Input
          aria-label="To"
          type="date"
          className="w-40"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </div>
      {rangeInvalid && (
        <p className="text-sm text-destructive">The end date is before the start date.</p>
      )}

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={query.data?.pages.flatMap((page) => page.items) ?? []}
            getRowId={(row) => row.id}
            isLoading={query.isPending && !rangeInvalid}
            empty="No audit entries match those filters."
          />
          <KeysetMore
            hasMore={query.hasNextPage}
            isFetching={query.isFetchingNextPage}
            onMore={() => void query.fetchNextPage()}
          />
        </>
      )}
    </>
  );
}
